import { useState } from 'react';
import {
  BookmarkPlus,
  Check,
  ChevronDown,
  Copy,
  Sparkles,
  Trash2,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import { Page, EmptyState } from '@/components/Page';
import { MedicalDisclaimer } from '@/components/MedicalDisclaimer';
import { AdviceList } from '@/features/advice/AdviceList';
import { useAdvice } from '@/features/advice/useAdvice';
import { NutrientRow } from '@/features/diary/NutrientRow';
import {
  removeMealEntry,
  repeatDay,
  setDayComplete,
  SLOTS,
  useDayComplete,
  useDayTotals,
  yesterdayOf,
} from '@/features/diary/useDiary';
import { saveMealFromEntries } from '@/features/diary/savedMeals';
import { isLlmOn } from '@/features/llm/isLlmOn';
import { useProfileState } from '@/features/profile/useProfile';
import { MAX_VISIBLE_ADVICE } from '@/engine/config';
import { scaleNutrients } from '@/engine/portions';
import { NUTRIENTS } from '@/engine/nutrients';
import type { MealSlot, NutrientKey, Targets } from '@/engine/types';
import { inputClass } from '@/components/ui/Field';
import type { MealRow } from '@/lib/db';
import { longDate, num, todayIso } from '@/lib/format';
import { cn } from '@/lib/utils';

const MACRO_ROWS: NutrientKey[] = ['protein', 'carbs', 'fat', 'fiber'];
const MICRO_ROWS = NUTRIENTS.filter((n) => n.window === 'semana').map((n) => n.key);

export default function Today() {
  const date = todayIso();
  const profileState = useProfileState();
  const day = useDayTotals(date);
  const complete = useDayComplete(date);
  const { advice, closing } = useAdvice(
    date,
    profileState.status === 'listo' ? profileState.profile : null,
    profileState.status === 'listo' ? profileState.targets : null,
  );
  const [showMicros, setShowMicros] = useState(false);
  const [showClosing, setShowClosing] = useState(false);
  const [copied, setCopied] = useState<number | null>(null);
  const [dismissedReminder, setDismissedReminder] = useState(false);
  const [savingSlot, setSavingSlot] = useState<MealSlot | null>(null);

  if (profileState.status !== 'listo' || !day) {
    return (
      <Page title="Hoy" subtitle={longDate(date)}>
        <p className="text-sm text-[var(--color-text-muted)]">Cargando…</p>
      </Page>
    );
  }

  const { profile, targets } = profileState;
  const llmOn = isLlmOn(profile);
  const { totals, coverage, entries } = day;

  const kcal = totals.kcal ?? 0;
  const remaining = Math.max(targets.kcal - kcal, 0);

  // Recordatorios por hora (SPEC 6.C). Dentro de la app: sin servidor de push
  // no hay notificaciones con la app cerrada, y prometerlas sería mentir.
  const hour = new Date().getHours();
  const closeHour = profile.reminders?.closeDayHour;
  const completeHour = profile.reminders?.dayCompleteHour;
  const closingOpen = showClosing || (closeHour != null && hour >= closeHour);
  const remindComplete =
    completeHour != null && hour >= completeHour && !complete && !dismissedReminder;

  async function copyYesterday() {
    const n = await repeatDay(yesterdayOf(date), date);
    setCopied(n);
  }

  return (
    <Page
      title="Hoy"
      subtitle={longDate(date)}
      action={
        <button
          type="button"
          onClick={() => void setDayComplete(date, !complete, targets)}
          aria-pressed={complete}
          className={cn(
            'tap flex shrink-0 items-center gap-1.5 rounded-xl border px-3 text-sm font-medium',
            complete
              ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
          )}
        >
          <Check aria-hidden="true" className="size-4" />
          {complete ? 'Día completo' : 'Marcar día'}
        </button>
      }
    >
      <div className="flex flex-col gap-5">
        {/* El cierre del día es BAJO DEMANDA (SPEC 6.C): tiene sentido cuando
            queda poco margen, no a media mañana. Va ARRIBA y le quita un hueco
            a la lista: es prioridad 1 y el tope de 3 avisos lo cuenta (SPEC 7). */}
        {remindComplete ? (
          <div
            role="status"
            className="flex items-start gap-3 rounded-[var(--radius-card)] border border-[var(--color-brand)] bg-[var(--color-brand-soft)] p-3 text-sm"
          >
            <p className="flex-1">
              Te falta marcar el día como completo. Solo los días completos cuentan para el
              histórico.
            </p>
            <button
              type="button"
              onClick={() => setDismissedReminder(true)}
              aria-label="Descartar el recordatorio"
              className="tap shrink-0 rounded-lg px-2 text-[var(--color-text-muted)]"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          </div>
        ) : null}

        {closing ? (
          closingOpen ? (
            <div className="flex flex-col gap-2">
              <AdviceList advice={[closing]} title="Para cerrar el día" rewrite={llmOn} />
              {remaining > targets.kcal * 0.35 ? (
                <p className="text-xs text-[var(--color-text-muted)]">
                  Todavía te queda buena parte del día. Esta propuesta cuadra los huecos que tienes
                  ahora; no pretende sustituir a tus comidas.
                </p>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowClosing(true)}
              className="tap flex items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-border)] px-4 text-sm font-medium text-[var(--color-text-muted)]"
            >
              <Sparkles aria-hidden="true" className="size-4" />
              Cerrar el día
            </button>
          )
        ) : null}

        <AdviceList
          advice={closingOpen && closing ? advice.slice(0, MAX_VISIBLE_ADVICE - 1) : advice}
          rewrite={llmOn}
        />

        {!profile.hideCalories ? (
          <section
            aria-label="Calorías del día"
            className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
          >
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold tabular">{num(kcal)}</p>
                <p className="text-xs text-[var(--color-text-muted)]">
                  de {num(targets.kcal)} kcal
                </p>
              </div>
              <p className="text-right text-sm text-[var(--color-text-muted)] tabular">
                {num(remaining)} kcal
                <br />
                <span className="text-xs">restantes</span>
              </p>
            </div>
            <div
              role="progressbar"
              aria-valuenow={Math.round((kcal / targets.kcal) * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Porcentaje de calorías consumidas"
              className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-surface-2)]"
            >
              <div
                className={cn(
                  'h-full rounded-full',
                  kcal > targets.kcal ? 'bg-[var(--color-warn)]' : 'bg-[var(--color-brand)]',
                )}
                style={{ width: `${Math.min((kcal / targets.kcal) * 100, 100)}%` }}
              />
            </div>
          </section>
        ) : null}

        <section aria-labelledby="macros">
          <h2 id="macros" className="mb-1 text-sm font-semibold">
            Macros y fibra
          </h2>
          <ul className="divide-y divide-[var(--color-border)]">
            {MACRO_ROWS.map((key) => (
              <NutrientRow
                key={key}
                nutrientKey={key}
                value={totals[key]}
                target={macroTarget(key, targets)}
                coverage={coverage[key]}
              />
            ))}
            <NutrientRow
              nutrientKey="saturatedFat"
              value={totals.saturatedFat}
              limit={targets.upperLimits.saturatedFat}
              coverage={coverage.saturatedFat}
            />
            <NutrientRow
              nutrientKey="sodium"
              value={totals.sodium}
              limit={targets.upperLimits.sodium}
              coverage={coverage.sodium}
            />
          </ul>
        </section>

        <section aria-labelledby="micros">
          <button
            type="button"
            onClick={() => setShowMicros((v) => !v)}
            aria-expanded={showMicros}
            className="tap flex w-full items-center justify-between text-left"
          >
            <h2 id="micros" className="text-sm font-semibold">
              Micronutrientes
            </h2>
            <ChevronDown
              aria-hidden="true"
              className={cn('size-5 transition-transform', showMicros && 'rotate-180')}
            />
          </button>
          {showMicros ? (
            <>
              <p className="mt-1 mb-1 text-xs text-[var(--color-text-muted)]">
                Los micros se valoran con la media de los últimos 7 días; aquí ves solo el total de
                hoy.
              </p>
              <ul className="divide-y divide-[var(--color-border)]">
                {MICRO_ROWS.map((key) => (
                  <NutrientRow
                    key={key}
                    nutrientKey={key}
                    value={totals[key]}
                    target={targets.micros[key]}
                    coverage={coverage[key]}
                  />
                ))}
              </ul>
            </>
          ) : null}
        </section>

        <section aria-labelledby="comidas">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 id="comidas" className="text-sm font-semibold">
              Comidas
            </h2>
            <button
              type="button"
              onClick={() => void copyYesterday()}
              className="tap flex items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-[var(--color-brand-strong)]"
            >
              <Copy aria-hidden="true" className="size-4" />
              Repetir ayer
            </button>
          </div>

          {copied != null ? (
            <p role="status" className="mb-2 text-xs text-[var(--color-text-muted)]">
              {copied === 0 ? 'Ayer no registraste nada.' : `Copiados ${copied} registros de ayer.`}
            </p>
          ) : null}

          {entries.length === 0 ? (
            <EmptyState
              icon={<UtensilsCrossed aria-hidden="true" className="size-8" />}
              title="Aún no has registrado nada hoy"
              hint="Usa Añadir para buscar un alimento o escanear un código de barras."
            />
          ) : (
            <div className="flex flex-col gap-3">
              {SLOTS.map(({ value, label }) => {
                const rows = entries.filter((e) => e.slot === value);
                if (rows.length === 0) return null;
                const slotKcal = rows.reduce(
                  (sum, r) => sum + (scaleNutrients(r.per100g, r.grams).kcal ?? 0),
                  0,
                );

                return (
                  <div key={value}>
                    <div className="flex items-baseline justify-between">
                      <h3 className="text-xs font-semibold tracking-wide text-[var(--color-text-muted)] uppercase">
                        {label}
                      </h3>
                      <span className="flex items-center gap-2">
                        {!profile.hideCalories ? (
                          <span className="text-xs text-[var(--color-text-muted)] tabular">
                            {num(slotKcal)} kcal
                          </span>
                        ) : null}
                        {/* Guardar la franja entera: el mismo desayuno cinco
                            días a la semana son quince toques cada mañana. */}
                        <button
                          type="button"
                          onClick={() => setSavingSlot(value)}
                          aria-label={`Guardar ${label.toLowerCase()} como comida`}
                          className="tap rounded-lg px-1 text-[var(--color-text-muted)]"
                        >
                          <BookmarkPlus aria-hidden="true" className="size-4" />
                        </button>
                      </span>
                    </div>
                    <ul className="divide-y divide-[var(--color-border)]">
                      {rows.map((entry) => (
                        <li key={entry.id} className="flex items-center gap-2 py-2">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm">{entry.foodName}</p>
                            <p className="text-xs text-[var(--color-text-muted)] tabular">
                              {num(entry.grams)} g
                              {!profile.hideCalories
                                ? ` · ${num(scaleNutrients(entry.per100g, entry.grams).kcal ?? 0)} kcal`
                                : ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => void removeMealEntry(entry.id)}
                            aria-label={`Quitar ${entry.foodName}`}
                            className="tap rounded-lg px-2 text-[var(--color-text-muted)]"
                          >
                            <Trash2 aria-hidden="true" className="size-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <MedicalDisclaimer compact />
      </div>

      {savingSlot ? (
        <SaveMealDialog
          entries={entries.filter((e) => e.slot === savingSlot)}
          defaultName={SLOTS.find((s) => s.value === savingSlot)?.label ?? 'Comida'}
          onClose={() => setSavingSlot(null)}
        />
      ) : null}
    </Page>
  );
}

/** Pone nombre a la franja que se guarda. Sin nombre no se encuentra luego. */
function SaveMealDialog({
  entries,
  defaultName,
  onClose,
}: {
  entries: MealRow[];
  defaultName: string;
  onClose: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (saving || name.trim().length < 2) return;
    setSaving(true);
    try {
      await saveMealFromEntries(name, entries);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Guardar comida"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
    >
      <div className="safe-bottom w-full max-w-lg rounded-t-3xl border-t border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-base font-semibold">Guardar como comida</h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Se guardan {entries.length} alimentos con sus gramos. Podrás registrarlos de una vez desde
          «Añadir».
        </p>
        <input
          type="text"
          aria-label="Nombre de la comida"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={cn(inputClass, 'mt-3')}
        />
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="tap flex-1 rounded-xl border border-[var(--color-border)] px-4 text-sm font-medium"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving || name.trim().length < 2}
            onClick={() => void save()}
            className="tap flex-1 rounded-xl bg-[var(--color-brand-button)] px-4 text-sm font-semibold text-[var(--color-on-brand)] disabled:opacity-50"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function macroTarget(key: NutrientKey, targets: Targets): number | undefined {
  if (key === 'protein') return targets.proteinG;
  if (key === 'carbs') return targets.carbsG;
  if (key === 'fat') return targets.fatG;
  return targets.micros[key];
}
